import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Table, Card } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FaEye, FaArrowLeft, FaStore, FaMoneyBillWave, FaCheckCircle, FaClock, FaEdit } from "react-icons/fa";

// Professional Styles Component
const styles = {
  colors: {
    primary: '#2563eb',
    primaryLight: '#3b82f6',
    success: '#059669',
    warning: '#d97706',
    danger: '#dc2626',
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827'
    }
  },

  summaryCard: (bgColor, textColor = '#ffffff') => ({
    background: bgColor,
    color: textColor,
    borderRadius: '12px',
    border: 'none',
    padding: '1.5rem',
    height: '100%',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }),

  modal: {
    header: {
      background: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      padding: '1.5rem 2rem',
      borderRadius: '12px 12px 0 0'
    },
    body: {
      padding: '2rem',
      background: '#ffffff'
    },
    footer: {
      background: '#f8fafc',
      borderTop: '1px solid #e2e8f0',
      padding: '1.25rem 2rem',
      borderRadius: '0 0 12px 12px'
    }
  },

  table: {
    header: {
      background: '#f1f5f9',
      color: '#475569',
      fontWeight: '600',
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '1rem',
      borderBottom: '2px solid #e2e8f0'
    },
    cell: {
      padding: '1rem',
      verticalAlign: 'middle',
      color: '#334155',
      fontSize: '0.95rem'
    }
  },

  infoCard: {
    background: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    padding: '2rem'
  },

  sectionHeader: {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#ffffff',
    padding: '1.25rem 1.5rem',
    borderRadius: '12px 12px 0 0',
    fontWeight: '600',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },

  statusBadge: (status) => {
    const styles = {
      paid: { bg: '#059669', color: '#ffffff', border: '#047857' },
      pending: { bg: '#d97706', color: '#ffffff', border: '#b45309' },
      overdue: { bg: '#dc2626', color: '#ffffff', border: '#b91c1c' },
      default: { bg: '#6b7280', color: '#ffffff', border: '#4b5563' }
    };
    const style = styles[status?.toLowerCase()] || styles.default;
    return {
      backgroundColor: style.bg,
      color: style.color,
      border: `1px solid ${style.border}`,
      padding: '0.375rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.875rem',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem'
    };
  },

  backButton: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    padding: '0.625rem 1.25rem',
    borderRadius: '8px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    textDecoration: 'none',
    transition: 'background-color 0.2s ease'
  }
};

export default function VendorShow() {
  const { t } = useContext(TranslatorContext);
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [balance, setBalance] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModal, setPurchaseModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const navigate = useNavigate();

  const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

  const hasPermission = (perm) => {
    return permissions.includes(perm);
  };

  useEffect(() => {
    fetchVendor();
  }, [id]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
      });

      // Set data from the response structure
      setVendor(res.data.vendor);
      setBalance(res.data.balance || 0);
      setPurchases(res.data.purchases || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openPurchaseModal = (purchase) => {
    setSelectedPurchase(purchase);
    setPurchaseModal(true);
  };

  const closePurchaseModal = () => {
    setSelectedPurchase(null);
    setPurchaseModal(false);
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount).replace('$', '');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return (
    <PageLayout>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </div>
          <p className="text-muted">{t("loading")}...</p>
        </div>
      </div>
    </PageLayout>
  );

  if (!vendor) return (
    <PageLayout>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="mb-3" style={{ fontSize: '4rem', color: '#d1d5db' }}>🔍</div>
          <h4 className="text-muted">{t("Vendor not found")}</h4>
          <Link to="/vendor-list" className="btn btn-primary mt-3">
            <FaArrowLeft className="me-2" />
            {t('back to vendors')}
          </Link>
        </div>
      </div>
    </PageLayout>
  );

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="mb-1 fw-bold" style={{ color: '#1f2937' }}>
            <FaStore className="me-2 text-primary" />
            {t('vendor details')}
          </h2>
          <p className="text-muted mb-0">{t('View vendor information and purchase history')}</p>
        </div>
        <Link
          to="/vendor-list"
          style={styles.backButton}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
        >
          <FaArrowLeft size={14} />
          {t('back to vendors')}
        </Link>
      </div>

      {/* Vendor Info Card */}
      <Row className="mb-4">
        <Col xl={12}>
          <Card style={styles.infoCard}>
            <div className="d-flex justify-content-between align-items-start mb-4 pb-3 border-bottom">
              <div>
                <h3 className="fw-bold mb-1" style={{ color: '#1f2937', fontSize: '1.75rem' }}>
                  {vendor.name}
                </h3>
                <span className="badge bg-light text-dark border">
                  {t('Vendor ID')}: #{vendor.id}
                </span>
              </div>
              <div className="text-end">
                <small className="text-muted d-block mb-1">{t('Total Transactions')}</small>
                <span className="fw-bold fs-4" style={{ color: '#2563eb' }}>
                  {purchases.length}
                </span>
              </div>
              <div className="text-end">
                <small className="text-muted d-block mb-1">{t('Amount Payable')}</small>
                <span className="fw-bold fs-4" style={{ color: '#2563eb' }}>
                  {balance}
                </span>
              </div>
            </div>

            <Row className="g-4 mb-2">
              <Col md={4}>
                <div className="p-3 rounded-3" style={{ background: '#f8fafc' }}>
                  <small className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    {t('email')}
                  </small>
                  <p className="mb-0 mt-1 fw-medium" style={{ color: '#374151', fontSize: '1rem' }}>
                    {vendor.email ? (
                      <a href={`mailto:${vendor.email}`} className="text-decoration-none" style={{ color: '#2563eb' }}>
                        {vendor.email}
                      </a>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 rounded-3" style={{ background: '#f8fafc' }}>
                  <small className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    {t('phone')}
                  </small>
                  <p className="mb-0 mt-1 fw-medium" style={{ color: '#374151', fontSize: '1rem' }}>
                    {vendor.phone ? (
                      <a href={`tel:${vendor.phone}`} className="text-decoration-none" style={{ color: '#2563eb' }}>
                        {vendor.phone}
                      </a>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 rounded-3" style={{ background: '#f8fafc' }}>
                  <small className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    {t('address')}
                  </small>
                  <p className="mb-0 mt-1 fw-medium" style={{ color: '#374151', fontSize: '1rem' }}>
                    {vendor.address || <span className="text-muted">-</span>}
                  </p>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>



      {/* Purchases Table */}
      <Row>
        <Col xl={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <div style={styles.sectionHeader}>
              <FaStore />
              {t('purchase history')}
            </div>
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={styles.table.header} className="ps-4">#</th>
                    <th style={styles.table.header}>{t('purchase number')}</th>
                    <th style={styles.table.header}>{t('purchase date')}</th>
                    <th style={styles.table.header}>{t('total amount')}</th>
                    <th style={styles.table.header}>{t('status')}</th>
                    <th style={styles.table.header} className="text-center pe-4">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length > 0 ? (
                    purchases.map((purchase, index) => (
                      <tr key={purchase.id} className="border-bottom">
                        <td style={{ ...styles.table.cell, paddingLeft: '1.5rem' }} className="fw-medium text-muted">
                          {index + 1}
                        </td>
                        <td style={styles.table.cell}>
                          <span className="fw-semibold" style={{ color: '#2563eb', fontSize: '0.95rem' }}>
                            {purchase.purchase_number}
                          </span>
                        </td>
                        <td style={styles.table.cell}>
                          <span className="text-muted">
                            {formatDate(purchase.purchase_date)}
                          </span>
                        </td>
                        <td style={styles.table.cell}>
                          <span className="fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>
                            {formatCurrency(purchase.total_amount)}
                          </span>
                        </td>
                        <td style={styles.table.cell}>
                          <span style={styles.statusBadge(purchase.status)}>
                            {purchase.status === 'paid' && <FaCheckCircle size={12} />}
                            {purchase.status === 'pending' && <FaClock size={12} />}
                            {purchase.status}
                          </span>
                        </td>
                        <td style={{ ...styles.table.cell, paddingRight: '1.5rem' }} className="text-center">
                          <button
                            className="btn btn-primary btn-sm m-1"
                            onClick={() => openPurchaseModal(purchase)}
                            
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#2563eb';
                              e.currentTarget.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#2563eb';
                            }}
                          >
                            <FaEye size={14} />
                          </button>
                          {hasPermission("Purchases edit") && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm m-1"
                              onClick={() => navigate(`/purchase-edit/${purchase.id}`)}
                              title="Edit"
                              
                            >
                              <FaEdit size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <div className="mb-3" style={{ fontSize: '3rem', color: '#d1d5db' }}>📦</div>
                        <p className="text-muted mb-0">{t('No purchases found')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Purchase Modal */}
      <Modal
        show={purchaseModal}
        onHide={closePurchaseModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton style={styles.modal.header}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <div style={{
              background: '#dbeafe',
              borderRadius: '8px',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FaStore size={20} color="#2563eb" />
            </div>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                {t('purchase details')}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 'normal' }}>
                {selectedPurchase?.purchase_number}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={styles.modal.body}>
          {selectedPurchase && (
            <div>
              {/* Purchase Info Cards */}
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <div className="p-3 rounded-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <small className="text-muted text-uppercase fw-semibold d-block mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      {t('purchase date')}
                    </small>
                    <span className="fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>
                      {formatDate(selectedPurchase.purchase_date)}
                    </span>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 rounded-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <small className="text-muted text-uppercase fw-semibold d-block mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      {t('total amount')}
                    </small>
                    <span className="fw-bold" style={{ color: '#2563eb', fontSize: '1.25rem' }}>
                      {formatCurrency(selectedPurchase.total_amount)}
                    </span>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 rounded-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <small className="text-muted text-uppercase fw-semibold d-block mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      {t('paid amount')}
                    </small>
                    <span className="fw-bold" style={{ color: '#059669', fontSize: '1.25rem' }}>
                      {formatCurrency(selectedPurchase.paid_amount)}
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Products Section */}
              <div className="mb-4">
                <h6 className="mb-3 d-flex align-items-center gap-2" style={{
                  color: '#1f2937',
                  fontWeight: '600',
                  fontSize: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  <span style={{
                    background: '#dbeafe',
                    borderRadius: '6px',
                    padding: '0.375rem',
                    display: 'inline-flex'
                  }}>
                    <FaStore size={16} color="#2563eb" />
                  </span>
                  {t('products')}
                </h6>
                <div className="table-responsive rounded-3 border">
                  <Table striped hover size="sm" className="mb-0">
                    <thead style={{  color: '#fff !important' }}>
                      <tr style={{ background: '#f1f5f9', color : '#ffffff !important'}}>
                        <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#ffff !important', fontSize: '0.875rem' }}>
                          {t('product')}
                        </th>
                        <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#ffff !important', fontSize: '0.875rem', textAlign: 'center' }}>
                          {t('quantity')}
                        </th>
                        <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#ffff !important', fontSize: '0.875rem', textAlign: 'right' }}>
                          {t('purchase price')}
                        </th>
                        <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#ffff !important', fontSize: '0.875rem', textAlign: 'right' }}>
                          {t('sale price')}
                        </th>
                        <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#ffff !important', fontSize: '0.875rem', textAlign: 'right' }}>
                          {t('total price')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchase.purchase_summary && selectedPurchase.purchase_summary.length > 0 ? (
                        selectedPurchase.purchase_summary.map((item, idx) => (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                            <td style={{ padding: '0.875rem 1rem', fontWeight: '500', color: '#1f2937' }}>
                              {item.name}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'center', color: '#6b7280' }}>
                              {item.qty}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: '#374151', fontWeight: '500' }}>
                              {formatCurrency(item.purchase_price)}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: '#374151', fontWeight: '500' }}>
                              {formatCurrency(item.sale_price)}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: '#059669', fontWeight: '600' }}>
                              {formatCurrency(item.purchase_price * item.qty)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            {t('No products found')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Payments Section */}
              {selectedPurchase.purchase_payments && selectedPurchase.purchase_payments.length > 0 && (
                <div>
                  <h6 className="mb-3 d-flex align-items-center gap-2" style={{
                    color: '#1f2937',
                    fontWeight: '600',
                    fontSize: '1rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <span style={{
                      background: '#dcfce7',
                      borderRadius: '6px',
                      padding: '0.375rem',
                      display: 'inline-flex'
                    }}>
                      <FaMoneyBillWave size={16} color='#059669' />
                    </span>
                    {t('payments')}
                  </h6>
                  <div className="table-responsive rounded-3 border">
                    <Table striped hover size="sm" className="mb-0">
                      <thead>
                        <tr style={{ background: '#f1f5f9' , color : '#ffffff !important'}}>
                          <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#fff !important', fontSize: '0.875rem' }}>
                            {t('payment method')}
                          </th>
                          <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#fff !important', fontSize: '0.875rem', textAlign: 'right' }}>
                            {t('amount')}
                          </th>
                          <th style={{ padding: '0.875rem 1rem', fontWeight: '600', color: '#fff !important', fontSize: '0.875rem', textAlign: 'right' }}>
                            {t('payment date')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPurchase.purchase_payments.map((payment, idx) => (
                          <tr key={payment.id} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                            <td style={{ padding: '0.875rem 1rem', color: '#1f2937', fontWeight: '500' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                background: '#eff6ff',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.875rem'
                              }}>
                                {payment.method?.name || '-'}
                              </span>
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: '#059669', fontWeight: '600' }}>
                              {formatCurrency(payment.amount)}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                              {formatDate(payment.payment_date)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={styles.modal.footer}>
          <button
            className="btn btn-secondary"
            onClick={closePurchaseModal}
            style={{
              padding: '0.625rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              border: 'none'
            }}
          >
            {t('close')}
          </button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}